import React, { useState } from "react";
import Modal from "./Modal";
import Button from "./Button";

// In-app replacement for window.confirm() — same "are you sure?" job, but
// styled to match the app instead of the browser's native "localhost says"
// dialog.
const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "primary",
}) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} size="sm">
      <p className="text-body text-content-secondary m-0">{message}</p>
      <div className="flex items-center justify-end gap-3 mt-6">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button variant={variant} onClick={handleConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

// Hook: gives back { confirm, dialogProps } — confirm(opts) returns a
// Promise<boolean> so call sites can swap in, unchanged otherwise:
//   const ok = await confirm({ title, message }); if (!ok) return;
// as a drop-in for the old `if (!window.confirm(...)) return;` shape.
export function useConfirm() {
  const [state, setState] = useState({ open: false, resolve: null, opts: {} });

  const confirm = (opts) =>
    new Promise((resolve) => {
      setState({ open: true, resolve, opts });
    });

  const handleConfirm = () => {
    state.resolve?.(true);
    setState({ open: false, resolve: null, opts: {} });
  };

  const handleCancel = () => {
    state.resolve?.(false);
    setState({ open: false, resolve: null, opts: {} });
  };

  const dialogProps = {
    isOpen: state.open,
    title: state.opts.title,
    message: state.opts.message,
    confirmLabel: state.opts.confirmLabel,
    cancelLabel: state.opts.cancelLabel,
    variant: state.opts.variant,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return { confirm, dialogProps };
}

export default ConfirmDialog;
